<template>
  <div class="reactions-area" ref="area">
    <div v-if="message.reactions && message.reactions.length" class="reactions-wrapper">
      <div class="reactions-display" @click="toggleReactionsPopup">
        <span
          v-for="reactionKey in groupedReactions"
          :key="reactionKey"
          class="emoji-reaction"
        >{{ reactionMap[reactionKey] || reactionKey }}</span>
        <span class="reaction-total">{{ message.reactions.length }}</span>
      </div>
      <div v-if="showReactionsPopup" class="reactions-popup">
        <div v-for="r in message.reactions" :key="r.id" class="reactions-popup__item">
          <router-link :to="{ name: 'member_profile', params: { user_id: r.user_id }, query: { from: $route.fullPath } }">
            <img :src="r.user.profile_image || 'https://media.iamconnected.com/profile_images/default.png'" class="reactions-popup__avatar" />
          </router-link>
          <span class="reactions-popup__name">{{ r.user.full_name }}</span>
          <span class="reactions-popup__emoji">{{ reactionMap[r.reaction] || r.reaction }}</span>
        </div>
      </div>
    </div>
    <div class="react-wrapper">
      <button @click="toggleEmojiPicker" title="React" class="btn react-trigger">
        <i class="far fa-smile"></i><span class="react-plus">+</span>
      </button>
      <div v-if="showEmojiPicker" class="emoji-picker-strip">
        <span
          v-for="key in reactionKeys"
          :key="key"
          @click="onReact(key)"
          :class="['emoji-btn', hasReacted(key) ? 'active' : '']"
        >{{ reactionMap[key] }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";

const REACTION_MAP = {
  heart: '❤️',
  infinity: '♾️',
  trident: '🔱',
  hugging_face: '🤗',
  thumbs_up: '👍',
  pray: '🙏',
  slightly_smiling_face: '🙂',
  open_mouth: '😮',
  deer: '🦌',
};

export default {
  name: 'MessageReactions',
  props: {
    message: { type: Object, required: true },
    activeChallengeId: { type: [String, Number], default: null },
  },
  data() {
    return {
      reactionMap: REACTION_MAP,
      reactionKeys: Object.keys(REACTION_MAP),
      showEmojiPicker: false,
      showReactionsPopup: false,
    };
  },
  computed: {
    groupedReactions() {
      return [...new Set((this.message.reactions || []).map(r => r.reaction))];
    },
  },
  methods: {
    hasReacted(reactionKey) {
      return (this.message.reactions || []).some(
        r => r.reaction === reactionKey && r.user_id === this.$store.state.profile.id
      );
    },
    toggleEmojiPicker() {
      this.showReactionsPopup = false;
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleReactionsPopup() {
      this.showEmojiPicker = false;
      this.showReactionsPopup = !this.showReactionsPopup;
    },
    onReact(reactionKey) {
      this.$emit('react', { messageId: this.message.id, reactionKey });
      this.showEmojiPicker = false;

      axios({
        method: 'post',
        url: `/program/${this.activeChallengeId}/react`,
        data: { message_id: this.message.id, reaction: reactionKey },
      }).catch(error => console.log(error));
    },
    handleOutsideClick(event) {
      if (this.$refs.area && !this.$refs.area.contains(event.target)) {
        this.showEmojiPicker = false;
        this.showReactionsPopup = false;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
};
</script>

<style scoped lang="scss">
.reactions-area {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  clear: both;

  .reactions-wrapper {
    position: relative;
    display: inline-block;

    .reactions-display {
      font-size: 14px;
      align-items: center;
      height: 26px;
      display: inline-flex;
      flex-wrap: wrap;
      gap: 0;
      border: 1px solid #d6b674;
      border-radius: 12px;
      padding: 2px 6px;
      cursor: pointer;

      .emoji-reaction {
        padding: 0 1px;
      }

      .reaction-total {
        color: #888;
        font-weight: 600;
        padding-left: 2px;
        align-self: center;
      }
    }

    .reactions-popup {
      position: absolute;
      bottom: calc(100% + 4px);
      left: 0;
      z-index: 200;
      background: #fff;
      border: 1px solid #dedede;
      border-radius: 12px;
      padding: 6px 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      min-width: 180px;
      white-space: nowrap;
      max-height: 220px;
      overflow-y: auto;
      overflow-x: hidden;

      &__item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 0;

        & + & {
          border-top: 1px solid #f0f0f0;
        }
      }

      &__avatar {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        object-fit: cover;
        flex-shrink: 0;
      }

      &__name {
        font-size: 0.85rem;
        flex: 1;
      }

      &__emoji {
        font-size: 1rem;
      }
    }
  }

  .react-wrapper {
    .react-trigger {
      color: #999;
      border: none;
      padding: 0;
      display: flex;
      align-items: center;
      line-height: 1;
      position: relative;

      .react-plus {
        position: absolute;
        top: -8px;
        right: -8px;
        font-size: 17px;
        background: transparent;
        border-radius: 50%;
        height: 12px;
        width: 12px;
        font-weight: 700;
        margin-left: 1px;
        vertical-align: super;
        line-height: 1;
      }
    }

    .emoji-picker-strip {
      position: absolute;
      left: 10px;
      bottom: 44px;
      z-index: 100;
      display: flex;
      gap: 6px;
      background: #fff;
      border: 1px solid #dedede;
      border-radius: 20px;
      padding: 4px 10px;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      max-width: 290px;
      overflow-y: auto;

      .emoji-btn {
        font-size: 1rem;
        height: 24px;
        cursor: pointer;
        border-radius: 50%;
        transition: background 0.15s;
        width: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 6px 4px 4px;

        &:hover,
        &.active {
          background: #d6b67433;
          outline: 2px solid #d6b674;
        }
      }
    }
  }
}
</style>
